import { useEffect, useCallback, useState } from 'react';

/**
 * Hook that warns users when they try to navigate away from a page with unsaved changes.
 * Uses `beforeunload` for browser tab close/refresh.
 * For in-app navigation, provides a modal API that the page can use.
 *
 * Note: Does NOT use useBlocker (requires data router / createBrowserRouter).
 *
 * @param {boolean} hasUnsavedChanges - Whether the form has unsaved changes
 * @returns {{ showModal: boolean, confirmNavigation: () => void, cancelNavigation: () => void }}
 */
export function useUnsavedChanges(hasUnsavedChanges) {
  const [showModal, setShowModal] = useState(false);
  const [pendingCallback, setPendingCallback] = useState(null);

  // beforeunload for browser close/refresh
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const confirmNavigation = useCallback(() => {
    setShowModal(false);
    if (pendingCallback) {
      pendingCallback();
      setPendingCallback(null);
    }
  }, [pendingCallback]);

  const cancelNavigation = useCallback(() => {
    setShowModal(false);
    setPendingCallback(null);
  }, []);

  const guardNavigation = useCallback(
    (callback) => {
      if (hasUnsavedChanges) {
        setPendingCallback(() => callback);
        setShowModal(true);
        return false;
      }
      callback();
      return true;
    },
    [hasUnsavedChanges]
  );

  return { showModal, confirmNavigation, cancelNavigation, guardNavigation };
}
